"use client";

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchLocation, fetchLocations, fetchTenantSettings } from "@/lib/api";
import { GoogleMapEmbed } from "@/components/GoogleMapEmbed";
import type { LocationDto } from "@/types/page";

export function MapBlock({ content }: { content: Record<string, unknown> }) {
  const heading = String(content.heading ?? "Visit us");
  const showAll = Boolean(content.showAll ?? true);
  const locationSlug = String(content.locationSlug ?? "");
  const [locations, setLocations] = useState<LocationDto[]>([]);

  const { data: tenant } = useQuery({
    queryKey: ["tenant-settings"],
    queryFn: fetchTenantSettings,
  });
  const apiKey = tenant?.settings?.googleMapsApiKey;

  useEffect(() => {
    if (!showAll && locationSlug) {
      fetchLocation(locationSlug).then((location) => setLocations(location ? [location] : [])).catch(() => setLocations([]));
      return;
    }
    fetchLocations().then(setLocations).catch(() => setLocations([]));
  }, [showAll, locationSlug]);

  const firstWithGeo = useMemo(
    () => locations.find((location) => location.latitude != null && location.longitude != null),
    [locations],
  );
  const mapUrl = firstWithGeo
    ? `https://www.google.com/maps/search/?api=1&query=${firstWithGeo.latitude},${firstWithGeo.longitude}`
    : locations[0]
      ? mapsUrl(locations[0])
      : "";

  if (!locations.length) return null;

  return (
    <section className="mx-auto max-w-6xl px-4 py-10">
      {heading && <h2 className="mb-6 text-3xl font-bold text-[var(--color-text)]">{heading}</h2>}
      <div className="grid gap-4 lg:grid-cols-[1fr_1.2fr]">
        <div className="grid gap-3">
          {locations.map((location) => (
            <div key={location.id} className="rounded-[var(--border-radius)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
              <h3 className="font-semibold text-[var(--color-text)]">{location.name}</h3>
              {location.description && <p className="mt-1 text-sm text-slate-600">{location.description}</p>}
              <p className="mt-3 text-sm text-slate-700">
                {location.addressLine1}
                {location.addressLine2 ? `, ${location.addressLine2}` : ""}
                <br />
                {location.city}, {location.region} {location.postalCode}
              </p>
              <div className="mt-3 flex flex-wrap gap-3 text-sm">
                {location.phone && <a className="text-[var(--color-primary)] hover:underline" href={`tel:${location.phone}`}>{location.phone}</a>}
                <a className="text-[var(--color-primary)] hover:underline" href={`/locations/${location.slug}`}>Details</a>
                <a className="text-[var(--color-primary)] hover:underline" href={mapsUrl(location)}>Directions</a>
              </div>
            </div>
          ))}
        </div>
        {apiKey && locations[0] ? (
          <GoogleMapEmbed apiKey={apiKey} location={locations[0]} />
        ) : (
          <a
            href={mapUrl}
            className="flex min-h-[320px] items-center justify-center rounded-[var(--border-radius)] border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-6 text-center text-sm font-medium text-[var(--color-primary)]"
          >
            Open map and directions
          </a>
        )}
      </div>
    </section>
  );
}

function mapsUrl(location: LocationDto): string {
  const query = [location.addressLine1, location.city, location.region, location.postalCode]
    .filter(Boolean)
    .join(", ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}
